import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Save, Plus, X, HelpCircle } from 'lucide-react';
import customFieldsService from '../services/customFieldsService';
import { useFacility } from '../context/FacilityContext';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Text (Single Line)' },
  { value: 'textarea', label: 'Text (Multi Line)' },
  { value: 'number', label: 'Number (Integer)' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Checkbox (Yes/No)' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'email', label: 'Email Address' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'url', label: 'Website URL' }
];

const CustomFieldEditor = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id || currentFacility?.id || '';
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [formData, setFormData] = useState({
    label: '', field_type: 'text', category: '', description: '', placeholder: '', default_value: '',
    is_required: false, is_searchable: false, show_in_list: false, options: [], validation_rules: [],
    display_order: 0, is_active: true
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [newOption, setNewOption] = useState('');
  const [rules, setRules] = useState({ min: '', max: '', min_length: '', max_length: '', regex: '' });

  const fetchField = useCallback(async () => {
    try {
      const { data } = await customFieldsService.getById(facilityId, id);
      setFormData(data);
      const newRules = { min: '', max: '', min_length: '', max_length: '', regex: '' };
      if (data.validation_rules) {
        data.validation_rules.forEach(rule => {
          if (rule.rule_type in newRules) newRules[rule.rule_type] = rule.value;
        });
      }
      setRules(newRules);
    } catch (err) {
      console.error('Error fetching field:', err);
      setError('Failed to load field definition.');
    } finally {
      setInitialLoading(false);
    }
  }, [id, facilityId]);

  useEffect(() => { if (!isNew) fetchField(); }, [isNew, fetchField]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleRuleChange = (e) => {
    const { name, value } = e.target;
    setRules(prev => ({ ...prev, [name]: value }));
  };

  const addOption = () => {
    if (newOption.trim()) {
      setFormData(prev => ({ ...prev, options: [...(prev.options || []), newOption.trim()] }));
      setNewOption('');
    }
  };

  const removeOption = (index) => {
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const validation_rules = [];
      if (formData.is_required) {
        validation_rules.push({ rule_type: 'required', value: true, error_message: `${formData.label} is required` });
      }
      Object.entries(rules).forEach(([type, value]) => {
        if (value !== '' && value !== null) {
          validation_rules.push({ rule_type: type, value: type === 'regex' ? value : Number(value) });
        }
      });
      const payload = { ...formData, validation_rules };
      if (isNew) {
        await customFieldsService.create(facilityId, payload);
        navigate('/settings/custom-fields');
      } else {
        await customFieldsService.update(facilityId, id, payload);
        // Stay on the field being edited.
        toast.success('Field saved.');
      }
    } catch (err) {
      console.error('Error saving field:', err);
      setError(err.response?.data?.detail || 'Failed to save field.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const showOptions = ['select', 'multi_select'].includes(formData.field_type);
  const showMinMax = ['number', 'decimal'].includes(formData.field_type);
  const showLength = ['text', 'textarea', 'email', 'url', 'phone'].includes(formData.field_type);

  const inputClass = "w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all";
  const labelClass = "block text-[13px] font-medium text-slate-700 mb-1.5";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">{isNew ? 'New Custom Field' : `Edit: ${formData.label}`}</h1>
        <p className="text-[13px] text-slate-400 mt-0.5">{isNew ? 'Define a new custom field' : 'Update field configuration'}</p>
      </div>

      <form onSubmit={handleSubmit} className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        {error && (
          <div className="m-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">{error}</div>
        )}

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Field Label <span className="text-red-500">*</span></label>
              <input type="text" name="label" value={formData.label} onChange={handleChange} required className={inputClass} placeholder="e.g. Current Military Base" />
              <p className="mt-1 text-[11px] text-slate-400">Used to generate the unique Field ID.</p>
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <input type="text" name="category" value={formData.category} onChange={handleChange} list="categories" className={inputClass} placeholder="e.g. Military" />
              <datalist id="categories">
                <option value="Military" /><option value="Property" /><option value="Personal" /><option value="Business" />
              </datalist>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <input type="text" name="description" value={formData.description} onChange={handleChange} className={inputClass} placeholder="Help text shown to users filling out the form" />
            </div>
            <div>
              <label className={labelClass}>Field Type <span className="text-red-500">*</span></label>
              <select name="field_type" value={formData.field_type} onChange={handleChange} disabled={!isNew} className={`${inputClass} ${!isNew ? 'bg-slate-50 cursor-not-allowed' : ''}`}>
                {FIELD_TYPES.map(type => (<option key={type.value} value={type.value}>{type.label}</option>))}
              </select>
              {!isNew && <p className="mt-1 text-[11px] text-amber-600">Field type cannot be changed after creation.</p>}
            </div>
            <div>
              <label className={labelClass}>Placeholder</label>
              <input type="text" name="placeholder" value={formData.placeholder} onChange={handleChange} className={inputClass} placeholder="Example value hint" />
            </div>
          </div>

          <hr className="border-slate-100" />

          {showOptions && (
            <div>
              <p className="text-[13px] font-semibold text-slate-700 mb-3">Allowed Options</p>
              <div className="rounded-lg bg-slate-50/50 p-3">
                <div className="flex gap-2 mb-3">
                  <input
                    type="text" value={newOption} onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                    className={inputClass} placeholder="Type an option and press Enter"
                  />
                  <button type="button" onClick={addOption} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors">Add</button>
                </div>
                {(!formData.options || formData.options.length === 0) ? (
                  <p className="text-[13px] text-slate-400 text-center py-2">No options added yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {formData.options.map((opt, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white px-3 py-2 rounded-lg border border-slate-200/80">
                        <span className="text-[13px] text-slate-700">{opt}</span>
                        <button type="button" onClick={() => removeOption(idx)} className="text-slate-400 hover:text-red-500 transition-colors">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <p className="text-[13px] font-semibold text-slate-700 mb-3">Validation & Behavior</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              {[
                { name: 'is_required', label: 'Required Field' },
                { name: 'is_searchable', label: 'Searchable' },
                { name: 'show_in_list', label: 'Show in Customer List' }
              ].map(({ name, label }) => (
                <label key={name} className="inline-flex items-center gap-2">
                  <input type="checkbox" name={name} checked={formData[name]} onChange={handleChange} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500/20" />
                  <span className="text-[13px] text-slate-600">{label}</span>
                </label>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {showMinMax && (
                <>
                  <div><label className={labelClass}>Min Value</label><input type="number" name="min" value={rules.min} onChange={handleRuleChange} className={inputClass} /></div>
                  <div><label className={labelClass}>Max Value</label><input type="number" name="max" value={rules.max} onChange={handleRuleChange} className={inputClass} /></div>
                </>
              )}
              {showLength && (
                <>
                  <div><label className={labelClass}>Min Length (chars)</label><input type="number" name="min_length" value={rules.min_length} onChange={handleRuleChange} className={inputClass} /></div>
                  <div><label className={labelClass}>Max Length (chars)</label><input type="number" name="max_length" value={rules.max_length} onChange={handleRuleChange} className={inputClass} /></div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Regex Pattern</label>
                    <div className="flex gap-2">
                      <input type="text" name="regex" value={rules.regex} onChange={handleRuleChange} placeholder="e.g. ^[A-Z]{2}\d{4}$" className={`${inputClass} font-mono`} />
                      <div className="flex items-center text-slate-400"><HelpCircle className="h-4 w-4" /></div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 bg-slate-50/50 border-t border-slate-100 flex justify-end gap-2">
          <Link to="/settings/custom-fields" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors">Cancel</Link>
          <button type="submit" disabled={loading} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> {loading ? 'Saving...' : 'Save Field'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomFieldEditor;
